import {
  useListAtsConnections,
  useGetAtsConnection,
  useCreateAtsConnectSession,
  useCreateAtsReconnectSession,
  useDisconnectAtsConnection,
  useFinalizeAtsConnection,
  getListAtsConnectionsQueryKey,
} from "@/lib/api/generated";
import type { AtsConnection } from "../types";

export { getListAtsConnectionsQueryKey };

/** Poll cadence for surfaces that actively watch a connection change state
 *  (e.g. the settings page watching an OAuth reconnect flip to `active`). */
export const CONNECTIONS_POLL_MS = 15_000;

interface AtsConnectionsPollOptions {
  /** Refetch cadence in ms, or `false` (default) for no polling. Connection
   *  identity/logo is effectively static, so most surfaces (job header, pipeline
   *  cells, wizard) don't poll — only the settings connections page opts in to
   *  watch reconnect/disconnect transitions live. */
  pollMs?: number | false;
}

export function useAtsConnections({ pollMs = false }: AtsConnectionsPollOptions = {}) {
  const query = useListAtsConnections({
    query: { refetchInterval: pollMs },
  });
  return {
    connections: (query.data?.connections ?? []) as unknown as AtsConnection[], // raw API snapshot is normalized to the view-model at the capability-matrix layer (Task 8)
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useAtsConnection(
  id: string | null,
  { pollMs = false }: AtsConnectionsPollOptions = {},
) {
  const query = useGetAtsConnection(id ?? "", {
    query: { enabled: !!id, refetchInterval: pollMs },
  });
  return {
    connection: (query.data ?? null) as unknown as AtsConnection | null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useConnectSession() {
  return useCreateAtsConnectSession();
}

export function useReconnectSession() {
  return useCreateAtsReconnectSession();
}

export function useDisconnectConnection() {
  return useDisconnectAtsConnection();
}

export function useFinalizeConnection() {
  return useFinalizeAtsConnection();
}
